import React, { useEffect, useState, cloneElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UserIcon, BookIcon, HeartIcon, FlameIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, BookOpenIcon, MapPinIcon, LinkIcon, ZapIcon, BookmarkIcon, GlobeIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
// Type definitions for our data
type CategoryKey = 'all' | 'romance' | 'fantasy' | 'mystery' | 'scifi' | 'horror' | 'general';
interface Category {
  key: CategoryKey | 'all';
  label: string;
  icon: JSX.Element;
  color: string;
}
interface Character {
  id: string;
  name: string;
  category: CategoryKey;
  count: number;
  popular: boolean;
  description?: string;
  traits?: string[];
}
const Characters: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<'popularity' | 'alphabetical' | 'bookCount' | 'viewAll'>('alphabetical');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Sync selectedCategory from URL params reactively
  useEffect(() => {
    const category = searchParams.get('category');
    setSelectedCategory(category as CategoryKey || 'all');
  }, [searchParams]);
  // Categories with their associated colors and icons
  const categories: Category[] = [{
    key: 'all',
    label: 'All Character Types',
    icon: <UserIcon className="h-4 w-4" data-id="element-8193" data-mp="333w7ex29mfnt" />,
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    key: 'romance',
    label: 'Romance',
    icon: <HeartIcon className="h-4 w-4" data-id="element-8194" data-mp="3cuh9iuh5e2bh" />,
    color: 'bg-pink-100 text-pink-800'
  }, {
    key: 'fantasy',
    label: 'Fantasy',
    icon: <BookIcon className="h-4 w-4" data-id="element-8195" data-mp="2p5pl8e6oi43z" />,
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    key: 'mystery',
    label: 'Mystery & Thriller',
    icon: <BookIcon className="h-4 w-4" data-id="element-8196" data-mp="2y5ckatbxqxtv" />,
    color: 'bg-amber-100 text-amber-800'
  }, {
    key: 'scifi',
    label: 'Sci-Fi',
    icon: <BookIcon className="h-4 w-4" data-id="element-8197" data-mp="2t4yezj1py1zj" />,
    color: 'bg-cyan-100 text-cyan-800'
  }, {
    key: 'horror',
    label: 'Horror',
    icon: <BookIcon className="h-4 w-4" data-id="element-8198" data-mp="8gj1c43604hp" />,
    color: 'bg-red-100 text-red-800'
  }, {
    key: 'general',
    label: 'General',
    icon: <BookIcon className="h-4 w-4" data-id="element-8199" data-mp="gwi1g4yu936c" />,
    color: 'bg-gray-100 text-gray-800'
  }];
  // Sample characters data
  const characters: Character[] = [
  // Romance Character Types
  {
    id: '1',
    name: 'Alpha Male',
    category: 'romance',
    count: 356,
    popular: true,
    description: 'Dominant, protective, and often possessive male lead.',
    traits: ['Protective', 'Dominant', 'Possessive', 'Strong']
  }, {
    id: '2',
    name: 'Cinnamon Roll',
    category: 'romance',
    count: 289,
    popular: true,
    description: 'Sweet, kind-hearted character who is pure and wholesome.',
    traits: ['Sweet', 'Kind', 'Supportive', 'Gentle']
  }, {
    id: '3',
    name: 'Bad Boy',
    category: 'romance',
    count: 245,
    popular: true,
    description: 'Rebellious character with a troubled past or reputation.',
    traits: ['Rebellious', 'Troubled', 'Brooding', 'Mysterious']
  }, {
    id: '4',
    name: 'Sunshine',
    category: 'romance',
    count: 234,
    popular: true,
    description: 'Perpetually cheerful and optimistic character.',
    traits: ['Cheerful', 'Optimistic', 'Friendly', 'Warm']
  }, {
    id: '5',
    name: 'Grumpy',
    category: 'romance',
    count: 212,
    popular: true,
    description: 'Irritable character with a soft heart underneath.',
    traits: ['Irritable', 'Reserved', 'Protective', 'Hidden depth']
  },
  // Fantasy Character Types
  {
    id: '6',
    name: 'Chosen One',
    category: 'fantasy',
    count: 267,
    popular: true,
    description: 'Character destined for greatness or to fulfill a prophecy.',
    traits: ['Destined', 'Special powers', 'Reluctant hero']
  }, {
    id: '7',
    name: 'Mentor',
    category: 'fantasy',
    count: 245,
    popular: true,
    description: 'Wise guide who trains or advises the protagonist.',
    traits: ['Wise', 'Experienced', 'Guiding', 'Mysterious']
  }, {
    id: '8',
    name: 'Reluctant Hero',
    category: 'fantasy',
    count: 213,
    popular: true,
    description: 'Character who initially refuses the call to adventure.',
    traits: ['Hesitant', 'Ordinary', 'Growth arc', 'Courageous']
  },
  // Mystery & Thriller Character Types
  {
    id: '9',
    name: 'Detective',
    category: 'mystery',
    count: 223,
    popular: true,
    description: 'Character who investigates and solves crimes or mysteries.',
    traits: ['Observant', 'Analytical', 'Determined', 'Clever']
  }, {
    id: '10',
    name: 'Unreliable Narrator',
    category: 'mystery',
    count: 167,
    popular: true,
    description: 'Narrator whose credibility is compromised.',
    traits: ['Deceptive', 'Biased', 'Secretive', 'Complicated']
  },
  // Sci-Fi Character Types
  {
    id: '11',
    name: 'AI/Robot',
    category: 'scifi',
    count: 189,
    popular: true,
    description: 'Artificial intelligence or robotic character.',
    traits: ['Logical', 'Learning humanity', 'Evolving', 'Curious']
  }, {
    id: '12',
    name: 'Space Captain',
    category: 'scifi',
    count: 178,
    popular: true,
    description: 'Leader of a spacecraft or space-based mission.',
    traits: ['Leadership', 'Decisive', 'Brave', 'Resourceful']
  },
  // Horror Character Types
  {
    id: '13',
    name: 'Final Girl',
    category: 'horror',
    count: 167,
    popular: true,
    description: 'Character troubled by supernatural elements or past trauma.',
    traits: ['Traumatized', 'Sensitive', 'Perceptive', 'Isolated']
  }, {
    id: '14',
    name: 'Haunted Protagonist',
    category: 'horror',
    count: 134,
    popular: true,
    description: 'Character troubled by supernatural elements or past trauma.',
    traits: ['Traumatized', 'Sensitive', 'Perceptive', 'Isolated']
  },
  // General Character Types
  {
    id: '15',
    name: 'Anti-Hero',
    category: 'general',
    count: 245,
    popular: true,
    description: 'Protagonist who lacks conventional heroic qualities.',
    traits: ['Morally ambiguous', 'Selfish', 'Complex', 'Flawed']
  }, {
    id: '16',
    name: 'Sidekick',
    category: 'general',
    count: 189,
    popular: true,
    description: 'Supporting character who assists the protagonist.',
    traits: ['Loyal', 'Supportive', 'Comic relief', 'Resourceful']
  }];
  // Filter and sort characters based on user selections
  const filteredCharacters = characters.filter(character => {
    // Filter by category
    if (selectedCategory !== 'all' && character.category !== selectedCategory) {
      return false;
    }
    // Filter by search query
    if (searchQuery && !character.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
  // Sort the filtered characters
  const sortedCharacters = [...filteredCharacters].sort((a, b) => {
    if (sortOption === 'alphabetical') {
      return a.name.localeCompare(b.name);
    } else if (sortOption === 'bookCount') {
      return b.count - a.count;
    } else if (sortOption === 'viewAll') {
      // For "View All", sort alphabetically regardless of category
      return a.name.localeCompare(b.name);
    } else {
      // Default: sort by popularity then by count
      if (a.popular !== b.popular) {
        return a.popular ? -1 : 1;
      }
      return b.count - a.count;
    }
  });
  // Group characters by category for display (only if not in "View All" mode)
  const groupedCharacters: Record<CategoryKey, Character[]> = {
    romance: [],
    fantasy: [],
    mystery: [],
    scifi: [],
    horror: [],
    general: [],
    all: []
  };
  sortedCharacters.forEach(character => {
    groupedCharacters[character.category].push(character);
  });
  // Get the category color for a character
  const getCharacterColor = (category: CategoryKey): string => {
    if (darkMode) {
      switch (category) {
        case 'romance':
          return 'bg-pink-900/30 text-pink-300';
        case 'fantasy':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'mystery':
          return 'bg-amber-900/30 text-amber-300';
        case 'scifi':
          return 'bg-cyan-900/30 text-cyan-300';
        case 'horror':
          return 'bg-red-900/30 text-red-300';
        case 'general':
          return 'bg-gray-700 text-gray-300';
        default:
          return dk.badgeNeutral;
      }
    }
    const categoryObj = categories.find(c => c.key === category);
    return categoryObj?.color || 'bg-gray-100 text-gray-800';
  };
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    isActive: true,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    subItems: [{
      label: 'Sexual Orientation & Gender',
      href: '/representation?category=Sexual+Orientation+%26+Gender'
    }, {
      label: 'Race & Ethnicity',
      href: '/representation?category=Race+%26+Ethnicity'
    }, {
      label: 'Neurodiversity & Disability',
      href: '/representation?category=Neurodiversity+%26+Disability'
    }, {
      label: 'Mental Health',
      href: '/representation?category=Mental+Health'
    }]
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    subItems: [{
      label: 'Personal Growth',
      href: '/themes?category=Personal+Growth'
    }, {
      label: 'Destiny',
      href: '/themes?category=Destiny'
    }, {
      label: 'Power',
      href: '/themes?category=Power'
    }, {
      label: 'Morality',
      href: '/themes?category=Morality'
    }, {
      label: 'Relationships',
      href: '/themes?category=Relationships'
    }, {
      label: 'Identity',
      href: '/themes?category=Identity'
    }]
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  const renderCharacters = () => {
    if (sortOption === 'viewAll' || selectedCategory !== 'all' || searchQuery) {
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-8200" data-mp="1uacx8u3l86f9">
          {sortedCharacters.map(character => <Link key={character.id} to={`/tagged-books/${character.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`} className={`${getCharacterColor(character.category)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-8201" data-mp="2n1wl4rb4q8nl">
              <div className="flex justify-between items-center mb-1" data-id="element-8202" data-mp="16k8t8lmhm4nq">
                <div className="font-medium" data-id="element-8203" data-mp="11lxkke7xi073">{character.name}</div>
                <div className="text-xs opacity-80" data-id="element-8204" data-mp="134fooc07fcmg">
                  {character.count} books
                </div>
              </div>
              {character.description && <p className="text-xs mt-1 opacity-90" data-id="element-8205" data-mp="u49ep03psus1">
                  {character.description}
                </p>}
            </Link>)}
        </div>;
    }
    const labelMap: Record<string, string> = {};
    categories.forEach(c => {
      labelMap[c.key] = c.label;
    });
    const sortedEntries = sortEntriesAlphabetically(Object.entries(groupedCharacters).filter(([key]) => key !== 'all'), labelMap);
    return <div className="space-y-3" data-id="element-8206" data-mp="bbf047a074oc">
        {sortedEntries.map(([category, chars]) => {
        const categoryObj = categories.find(c => c.key === category);
        return <ContentAccordionSection key={category} title={categoryObj?.label || category} icon={cloneElement(categoryObj?.icon || <UserIcon className="h-5 w-5" data-id="element-8208" data-mp="3iz2cibrm067t" />, {
          className: 'h-5 w-5'
        })} itemCount={chars.length} data-id="element-8207" data-mp="1fu02eohweh8">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-8209" data-mp="3sx8dmr5ld8bm">
                {chars.map(character => <Link key={character.id} to={`/tagged-books/${character.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`} className={`${getCharacterColor(character.category as CategoryKey)} rounded-lg p-3 hover:shadow-md transition-shadow`} data-id="element-8210" data-mp="3nz6t01sip0y1">
                    <div className="flex justify-between items-center mb-1" data-id="element-8211" data-mp="ohytverx8yg6">
                      <div className="font-medium" data-id="element-8212" data-mp="ofw6l2i5ljf5">{character.name}</div>
                      <div className="text-xs opacity-80" data-id="element-8213" data-mp="khombxohdzz7">
                        {character.count} books
                      </div>
                    </div>
                    {character.description && <p className="text-xs mt-1 opacity-90" data-id="element-8214" data-mp="1vsv0hxkr6uzw">
                        {character.description}
                      </p>}
                  </Link>)}
              </div>
            </ContentAccordionSection>;
      })}
      </div>;
  };
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Characters'
    }, {
      value: 'romance',
      label: 'Romance'
    }, {
      value: 'fantasy',
      label: 'Fantasy'
    }, {
      value: 'mystery',
      label: 'Mystery & Thriller'
    }, {
      value: 'scifi',
      label: 'Sci-Fi'
    }, {
      value: 'horror',
      label: 'Horror'
    }, {
      value: 'general',
      label: 'General'
    }],
    value: selectedCategory,
    onChange: (val: string) => setSelectedCategory(val as CategoryKey)
  }, {
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'popularity',
      label: 'Popularity'
    }, {
      value: 'alphabetical',
      label: 'Alphabetical'
    }, {
      value: 'bookCount',
      label: 'Book Count'
    }, {
      value: 'viewAll',
      label: 'View All (flat list)'
    }],
    value: sortOption,
    onChange: (val: string) => setSortOption(val as typeof sortOption)
  }];
  const handleReset = () => {
    setSelectedCategory('all');
    setSortOption('popularity');
    setSearchQuery('');
  };
  return <div className={dk.page} data-id="element-8215" data-mp="3bef557a3dxia">
      <ContentPageHeader title="Characters" icon={getContentPageIcon('characters')} searchPlaceholder="Search characters..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-8216" data-mp="3be5m63ngsqm3" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8217" data-mp="308xw6u1gzv1p">
        <div className="flex gap-8" data-id="element-8218" data-mp="2v6yppbafoi0j">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-8219" data-mp="2va4fytustpxu">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={filterSections} onReset={handleReset} searchable={false} data-id="element-8220" data-mp="2hfk4cxt204qb" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-8221" data-mp="27k0bkk9sdm5s">
            {sortedCharacters.length === 0 ? <div className="text-center py-12" data-id="element-8222" data-mp="23iotr0x5m585">
                <p className={dk.textMuted} data-id="element-8223" data-mp="ixi3ouhu2g5v">
                  No characters found matching your criteria.
                </p>
              </div> : renderCharacters()}
          </main>
        </div>
      </div>
    </div>;
};
export default Characters;